import React, { useState } from "react";
import { X, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { quickRepliesApi } from "../../lib/api";

/**
 * TemplateManager — modal for managing personal + shared "Quick Reply"
 * templates. Agents can create/edit/delete their own templates; admins
 * can additionally create "shared" templates that everyone sees.
 *
 * Extracted from ChatsPage.jsx Apr 2026 as part of the refactor sprint.
 */
const TemplateManager = ({ templates, onClose, currentUser }) => {
  const [items, setItems] = useState(templates);
  const [editItem, setEditItem] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [scope, setScope] = useState("private");
  const [saving, setSaving] = useState(false);
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "super_admin";

  const resetForm = () => { setEditItem(null); setTitle(""); setContent(""); setScope("private"); };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return toast.error("Title and content required");
    setSaving(true);
    try {
      const data = { title: title.trim(), content: content.trim(), category: null, scope };
      if (editItem) {
        await quickRepliesApi.update(editItem.id, data);
        setItems((prev) => prev.map((i) => (i.id === editItem.id ? { ...i, ...data } : i)));
        toast.success("Updated");
      } else {
        const res = await quickRepliesApi.create(data);
        setItems((prev) => [...prev, res.data.template]);
        toast.success("Created");
      }
      resetForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await quickRepliesApi.delete(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
      if (editItem?.id === id) resetForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const isOwner = (t) => t.created_by === currentUser?.id || !t.created_by;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="template-manager-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[75vh] flex flex-col animate-scale-in">
        <div className="flex items-center justify-between p-4 border-b border-[--border]">
          <h2 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Quick Reply Templates</h2>
          <button onClick={onClose} className="btn-icon"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="p-3 bg-[--bg-cream] rounded-xl space-y-2">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Template name" className="form-input text-xs" data-testid="template-title-input" />
            <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Message content..." rows={2} className="form-input text-xs resize-none" data-testid="template-content-input" />
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-semibold text-[--text-muted] uppercase tracking-wider">Visibility:</label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="text-[11px] border border-[--border] rounded-md px-2 py-1 bg-white"
                data-testid="template-scope-select"
                disabled={scope === "shared" && !isAdmin}
              >
                <option value="private">Private (only me)</option>
                {isAdmin && <option value="shared">Shared (everyone)</option>}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={saving} className="btn-primary text-xs py-2 px-4" data-testid="save-template-btn">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                {editItem ? "Update" : "Add"}
              </button>
              {editItem && <button onClick={resetForm} className="btn-secondary text-xs py-2 px-3">Cancel</button>}
            </div>
          </div>
          {items.map((item) => {
            const ownable = isOwner(item) || isAdmin;
            return (
              <div key={item.id} className={`p-3 border rounded-xl transition-colors ${editItem?.id === item.id ? "border-[--accent] bg-[--accent-pale]" : "border-[--border] hover:border-[--accent-soft]"}`} data-testid={`template-item-${item.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <p className="font-medium text-[13px]">{item.title}</p>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${item.scope === "shared" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-gray-100 text-gray-600 border border-gray-200"}`}>
                        {item.scope === "shared" ? "SHARED" : "PRIVATE"}
                      </span>
                    </div>
                    <p className="text-[11px] text-[--text-secondary] mt-1">{item.content}</p>
                  </div>
                  {ownable && (
                    <div className="flex gap-1 ml-2">
                      <button onClick={() => { setEditItem(item); setTitle(item.title); setContent(item.content); setScope(item.scope || "private"); }} className="btn-icon w-7 h-7" data-testid={`edit-template-${item.id}`}><Pencil size={12} /></button>
                      <button onClick={() => handleDelete(item.id)} className="btn-icon w-7 h-7 hover:!bg-red-50 hover:!text-red-500" data-testid={`delete-template-${item.id}`}><Trash2 size={12} /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TemplateManager;
